import { Vector2, Vector2Like } from "three";
import { AABB } from "./aabb2d";

export const checkPointInsideAABB = (point: Vector2Like, aabb: AABB) => {
    return (
        point.x >= aabb.minX && point.x <= aabb.maxX && point.y >= aabb.minY && point.y <= aabb.maxY
    );
};

export const checkAABBIntersectsAABB = (aabb1: AABB, aabb2: AABB) => {
    if (aabb1.minX < aabb2.minX && aabb1.maxX < aabb2.minX) {
        return false;
    }

    if (aabb1.minX > aabb2.maxX && aabb1.maxX > aabb2.maxX) {
        return false;
    }

    if (aabb1.minY < aabb2.minY && aabb1.maxY < aabb2.minY) {
        return false;
    }

    if (aabb1.minY > aabb2.maxY && aabb1.maxY > aabb2.maxY) {
        return false;
    }

    return true;
};

export const checkAABBInsideAABB = (test: AABB, room: AABB) => {
    return (
        test.minX >= room.minX &&
        test.maxX <= room.maxX &&
        test.minY >= room.minY &&
        test.maxY <= room.maxY
    );
};
export const moveAABBtoPoint = (aabb: AABB, point: Vector2Like) => {
    return new AABB(
        point.x - aabb.width / 2,
        point.x + aabb.width / 2,
        point.y - aabb.height / 2,
        point.y + aabb.height / 2
    );
};

export const getAABBIntersectsSection = (
    aabb: AABB,
    p1: Vector2Like,
    p2: Vector2Like,
    result: Vector2
): Vector2 | undefined => {
    // both points are inside or outside aabb
    if (checkPointInsideAABB(p1, aabb) === checkPointInsideAABB(p2, aabb)) {
        return undefined;
    }

    // check intersection for minX
    let x = 0;
    let y = 0;
    let dx = 0;
    let dy = 0;
    result.set(0, 0);
    let hasIntersection = false;
    if ((p1.x < aabb.minX && p2.x > aabb.minX) || (p1.x > aabb.minX && p2.x < aabb.minX)) {
        const k = (aabb.minX - p1.x) / (p2.x - p1.x);
        result.set(aabb.minX, p1.y + k * (p2.y - p1.y));
        hasIntersection = true;
    }

    //check intersection for maxX
    if ((p1.x < aabb.maxX && p2.x > aabb.maxX) || (p1.x > aabb.maxX && p2.x < aabb.maxX)) {
        const k = (aabb.maxX - p1.x) / (p2.x - p1.x);
        x = aabb.maxX;
        y = p1.y + k * (p2.y - p1.y);
        dx = x - p1.x;
        dy = y - p1.y;
        if (!hasIntersection || dx * dx + dy * dy < result.lengthSq()) {
            hasIntersection = true;
            result.set(x, y);
        }
    }

    // check intersection for minY
    if ((p1.y < aabb.minY && p2.y > aabb.minY) || (p1.y > aabb.minY && p2.y < aabb.minY)) {
        const k = (aabb.minY - p1.y) / (p2.y - p1.y);
        x = p1.x + k * (p2.x - p1.x);
        y = aabb.minY;
        dx = x - p1.x;
        dy = y - p1.y;
        if (!hasIntersection || dx * dx + dy * dy < result.lengthSq()) {
            hasIntersection = true;
            result.set(x, y);
        }
    }

    // check intersection for maxY
    if ((p1.y < aabb.maxY && p2.y > aabb.maxY) || (p1.y > aabb.maxY && p2.y < aabb.maxY)) {
        const k = (aabb.maxY - p1.y) / (p2.y - p1.y);
        x = p1.x + k * (p2.x - p1.x);
        y = aabb.maxY;
        dx = x - p1.x;
        dy = y - p1.y;
        if (!hasIntersection || dx * dx + dy * dy < result.lengthSq()) {
            hasIntersection = true;
            result.set(x, y);
        }
    }

    return result;
};

export const fixAabbPosition = (aabb: AABB, room: AABB): AABB => {
    const result = new AABB(aabb.minX, aabb.maxX, aabb.minY, aabb.maxY);
    const width = result.width;
    const height = result.height;

    if (result.minX < room.minX) {
        result.minX = room.minX;
        result.maxX = result.minX + width;
    }

    if (result.maxX > room.maxX) {
        result.maxX = room.maxX;
        result.minX = result.maxX - width;
    }

    if (result.minY < room.minY) {
        result.minY = room.minY;
        result.maxY = result.minY + height;
    }

    if (result.maxY > room.maxY) {
        result.maxY = room.maxY;
        result.minY = result.maxY - height;
    }

    return result;
};

export const getRoomWallRotation = (
    cursor: Vector2Like,
    room: AABB,
    rotationDistance: number
): number | undefined => {
    const leftWallDistance = cursor.x - room.minX;
    const rightWallDistance = room.maxX - cursor.x;
    const topWallDistance = cursor.y - room.minY;
    const bottomWallDistance = room.maxY - cursor.y;

    const minDistance = Math.min(
        leftWallDistance,
        rightWallDistance,
        topWallDistance,
        bottomWallDistance
    );
    if (minDistance > rotationDistance) {
        return undefined;
    }

    if (leftWallDistance === minDistance) {
        return Math.PI;
    }

    if (rightWallDistance === minDistance) {
        return 0;
    }

    if (topWallDistance === minDistance) {
        return Math.PI / 2;
    }

    if (bottomWallDistance === minDistance) {
        return -Math.PI / 2;
    }

    return undefined;
};

export const stickAabbToRoom = (aabb: AABB, room: AABB, stickDistance: number): AABB => {
    const result = new AABB(aabb.minX, aabb.maxX, aabb.minY, aabb.maxY);
    const width = result.width;
    const height = result.height;

    if (aabb.minX < room.minX + stickDistance) {
        result.minX = room.minX;
        result.maxX = result.minX + width;
    }

    if (aabb.maxX > room.maxX - stickDistance) {
        result.maxX = room.maxX;
        result.minX = result.maxX - width;
    }

    if (aabb.minY < room.minY + stickDistance) {
        result.minY = room.minY;
        result.maxY = result.minY + height;
    }

    if (aabb.maxY > room.maxY - stickDistance) {
        result.maxY = room.maxY;
        result.minY = result.maxY - height;
    }

    return result;
};

export const fractureAabb = (room: AABB, hole: AABB): AABB[] => {
    if (!checkAABBIntersectsAABB(room, hole)) {
        return [room];
    }

    const fractions = Array<AABB>();
    if (hole.minX > room.minX && hole.minX < room.maxX) {
        fractions.push(new AABB(room.minX, hole.minX, room.minY, room.maxY));
    }

    if (hole.maxX < room.maxX && hole.maxX > room.minX) {
        fractions.push(new AABB(hole.maxX, room.maxX, room.minY, room.maxY));
    }

    if (hole.minY > room.minY && hole.minY < room.maxY) {
        fractions.push(new AABB(room.minX, room.maxX, room.minY, hole.minY));
    }

    if (hole.maxY < room.maxY && hole.maxY > room.minY) {
        fractions.push(new AABB(room.minX, room.maxX, hole.maxY, room.maxY));
    }

    if (fractions.length > 0) {
        return fractions;
    }

    // room is inside of hole
    if (checkAABBInsideAABB(room, hole)) {
        return [];
    }

    return [room];
};

export const fractureRoom = (room: AABB, holes: AABB[]): AABB[] => {
    let fractions = [room];
    if (holes.length === 0) {
        return fractions;
    }

    for (const hole of holes) {
        fractions = fractions.flatMap((fraction) => fractureAabb(fraction, hole));
    }

    const filteredFractions = Array<AABB>();
    for (let i = 0; i < fractions.length; i++) {
        const fraction = fractions[i];

        let isInside = false;
        for (let j = 0; j < fractions.length; j++) {
            if (i === j) {
                continue;
            }

            const otherFraction = fractions[j];
            if (checkAABBInsideAABB(fraction, otherFraction)) {
                isInside = true;
                break;
            }
        }

        if (!isInside) {
            filteredFractions.push(fraction);
        }
    }

    return filteredFractions;
};

export const filterRoomFractionsBySize = (fractions: AABB[], width: number, height: number) => {
    return fractions.filter((fraction) => fraction.width >= width && fraction.height >= height);
};

export const getAabbIntersection = (aabb1: AABB, aabb2: AABB) => {
    if (!checkAABBIntersectsAABB(aabb1, aabb2)) {
        return undefined;
    }

    const result = new AABB(
        Math.max(aabb1.minX, aabb2.minX),
        Math.min(aabb1.maxX, aabb2.maxX),
        Math.max(aabb1.minY, aabb2.minY),
        Math.min(aabb1.maxY, aabb2.maxY)
    );
    return result;
};

export const fixAabbPositionByRoomFractions = (aabb: AABB, roomFractions: AABB[]) => {
    const result = new AABB(aabb.minX, aabb.maxX, aabb.minY, aabb.maxY);

    let fractionWithGreatestIntersectionArea: AABB | undefined = undefined;
    let greatestIntersectionArea = 0;

    let minimalDistance = Infinity;
    let fractionWithMinimalDistance: AABB | undefined = undefined;
    const v2 = new Vector2(aabb.center.x, aabb.center.y);

    for (const fraction of roomFractions) {
        // it is enough to check if aabb is inside of one of the fractions
        if (checkAABBInsideAABB(aabb, fraction)) {
            return result;
        }

        const intersection = getAabbIntersection(aabb, fraction);
        if (intersection && intersection.area > 0) {
            const intersectionArea = intersection.area;
            if (intersectionArea > greatestIntersectionArea) {
                greatestIntersectionArea = intersectionArea;
                fractionWithGreatestIntersectionArea = fraction;
            }
            continue;
        }

        if (greatestIntersectionArea > 0) {
            continue;
        }

        // todo:
        const distance = v2.distanceTo(fraction.center);
        if (distance < minimalDistance) {
            minimalDistance = distance;
            fractionWithMinimalDistance = fraction;
        }
    }

    if (fractionWithGreatestIntersectionArea) {
        return fixAabbPosition(aabb, fractionWithGreatestIntersectionArea);
    }

    if (fractionWithMinimalDistance) {
        return fixAabbPosition(aabb, fractionWithMinimalDistance);
    }

    console.error("No suitable space found");

    return undefined;
};
